import React, { FC, useCallback, useEffect } from "react";

import {
  BaseComponentProps,
  KFooter as KFooterComponent,
  KFooterElementDirection,
  KFooterElementPosition,
  KFooterLinkType,
  Link,
  SocialNetworks as SocialNetworksComponent,
  useThemeColors,
} from "@keepeek/refront-components";
import { Box, Theme, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { SxProps } from "@mui/system";
import clsx from "clsx";
import { useRouter } from "next/router";

import { KIcon } from "../../../components/common/KIcon";
import {
  FooterWidget as FooterConfiguration,
  LanguageSwitcherPosition,
  Link as LinkFooter,
  LinkType,
  SocialNetworks,
} from "../../../models/configuration/components/footerWidget";
import KLanguageSwitcher, { showKLanguageSwitcher } from "../../KLanguageSwitcher";
import { getKLanguageSwitcherStyle } from "../../KLanguageSwitcher/utils";
import { WidgetUIState } from "../atoms";
import { useWidgetState } from "../hooks";
import { KWidgetProps } from "../models";
import { ComponentNextLink } from "../utils/parseInternalUrl";
import { componentSizeFooter } from "./utils";

export type KFooterWidgetProps = {
  widgetId: string;
  footerConfiguration: FooterConfiguration;
} & KWidgetProps &
  BaseComponentProps;

/**
 * Component allowing to make the link between the KFooter component and the Admin ReFront configuration
 *
 * @param footerConfiguration
 * @returns the KFooter component well configured
 */
const KFooterWidget: FC<React.PropsWithChildren<KFooterWidgetProps>> = function ({
  className,
  style,
  widgetId,
  footerConfiguration: {
    backgroundColor,
    color,
    borderColor,
    verticalPadding,
    linksPosition,
    container,
    languageSwitcherPosition = LanguageSwitcherPosition.Left,
    linksDirection,
    linksPadding,
    linksSize,
    linksListHeight,
    linksListWidth,
    linksUnderlined,
    linksUnderlinedOnHover,
    linksColorOnHover,
    linksByLocale,
    socialNetworks,
    socialNetworksLanguageDirection,
  },
  type,
}) {
  const { setWidgetState, widgetState } = useWidgetState({
    id: widgetId,
    type,
  });
  const { locale: currentLocale, locales } = useRouter();
  const { convertColor } = useThemeColors();
  const kLanguageSwitcherStyle = (_color?: string): SxProps<Theme> => {
    return {
      ...getKLanguageSwitcherStyle(_color || theme.palette.common.black),
      paddingRight:
        languageSwitcherPosition === LanguageSwitcherPosition.Right && !container ? "2rem" : "0px",
      paddingLeft:
        languageSwitcherPosition === LanguageSwitcherPosition.Left && !container ? "2rem" : "0px",
      "& .MuiInputBase-formControl": {
        fontSize: `${linksSize || 1}em`,
      },
    };
  };

  const getFooterLinksForCurrentLocaleIfPresents = useCallback(
    (_linksByLocale: any) => {
      const footerLinks: Array<Link> = [];
      const linksForCurrentLocale = _linksByLocale?.find(
        (lbl) => currentLocale?.toLocaleLowerCase() === lbl.locale?.toLocaleLowerCase(),
      );
      linksForCurrentLocale?.links?.forEach((l: LinkFooter) => {
        footerLinks.push({
          label: l.label,
          type: l.linkType === LinkType.Didomi ? KFooterLinkType.DIDOMI : KFooterLinkType.URL,
          href: l.href || "",
          openInNewTab: l.linkType === LinkType.Didomi ? false : l.openInNewTab || false,
        });
      });
      return footerLinks;
    },
    [currentLocale],
  );
  const getFooterSocialNetworksForCurrentLocaleIfPresents = useCallback(
    (_socialNetworks: SocialNetworks | undefined): SocialNetworksComponent => {
      const iconWithLinkByLanguage = _socialNetworks?.socialNetworksItems?.find(
        (socialNetworksItem) =>
          currentLocale?.toLocaleLowerCase() === socialNetworksItem.language?.toLocaleLowerCase(),
      );
      const getIconsWithLinks = (): SocialNetworksComponent["iconsWithLinks"] =>
        iconWithLinkByLanguage?.iconsWithLinks;
      const getLabelForIconsWithLinks = (): SocialNetworksComponent["label"] =>
        iconWithLinkByLanguage?.label;

      return {
        color: _socialNetworks?.color,
        iconsWithLinks: getIconsWithLinks(),
        label: getLabelForIconsWithLinks(),
      };
    },
    [currentLocale],
  );

  useEffect(() => {
    if (
      !getFooterLinksForCurrentLocaleIfPresents(linksByLocale).length &&
      !showKLanguageSwitcher(locales)
    ) {
      setWidgetState({ state: WidgetUIState.NULL });
    } else {
      setWidgetState({ state: WidgetUIState.RENDERED });
    }
  }, [
    getFooterLinksForCurrentLocaleIfPresents,
    linksByLocale,
    locales,
    setWidgetState,
    getFooterSocialNetworksForCurrentLocaleIfPresents,
    socialNetworks,
  ]);

  const theme = useTheme();
  // Take the last value here where we will have a 12/12 column position in componentSizeFooter
  const isAboveSm = useMediaQuery(theme.breakpoints.up("sm"));

  if (widgetState.state === WidgetUIState.RENDERED) {
    return (
      <KFooterComponent
        sx={{ width: "100%" }}
        className={clsx("KFooter", className)}
        style={style}
        links={getFooterLinksForCurrentLocaleIfPresents(linksByLocale)}
        socialNetworks={getFooterSocialNetworksForCurrentLocaleIfPresents(socialNetworks)}
        useContainer={container !== undefined ? container : true}
        backgroundColor={convertColor(backgroundColor)}
        color={convertColor(color)}
        borderColor={convertColor(borderColor)}
        verticalPadding={verticalPadding}
        linksPosition={linksPosition as unknown as KFooterElementPosition}
        linksDirection={linksDirection as unknown as KFooterElementDirection}
        linksPadding={linksPadding}
        linksSize={linksSize}
        linksUnderlined={linksUnderlined}
        linksUnderlinedOnHover={linksUnderlinedOnHover}
        linksListHeight={linksListHeight}
        linksListWidth={linksListWidth}
        linksColorOnHover={convertColor(linksColorOnHover)}
        leftComponent={
          languageSwitcherPosition === LanguageSwitcherPosition.Left && (
            <Box
              sx={{
                display: "flex",
                justifyContent: isAboveSm ? "flex-start" : "center",
              }}
            >
              <KLanguageSwitcher sx={kLanguageSwitcherStyle(convertColor(color))} />
            </Box>
          )
        }
        leftComponentSize={componentSizeFooter}
        rightComponent={
          languageSwitcherPosition === LanguageSwitcherPosition.Right && (
            // Depends on values in componentSizeFooter
            // Show the language switcher in far right when we're not in a 12/12 position
            <Box
              sx={{
                display: "flex",
                justifyContent: isAboveSm ? "flex-end" : "center",
              }}
            >
              <KLanguageSwitcher sx={kLanguageSwitcherStyle(convertColor(color))} />
            </Box>
          )
        }
        rightComponentSize={componentSizeFooter}
        ComponentNextLinkCallBack={ComponentNextLink}
        socialNetworksLanguageDirection={
          socialNetworksLanguageDirection as unknown as KFooterElementDirection
        }
        KIcon={KIcon}
      />
    );
  }
  return null;
};

export default KFooterWidget;
